import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDebounce } from 'react-use';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pagination } from './pagination';
import { RocheEndpoint } from '../../services/external-data/types';
import { useTranslation } from '../../hooks/use-translation';
import { AdobeTracking } from '../../tracking-services';
import { OnSiteSearch } from '../../tracking-services/types';

export interface SearchListProps {
  slot?: string;
  category?: string;
  tabName?: string;
  pageName?: string;
  queryName?: string;
  children?: ({ items }) => JSX.Element;
  searchTabs?: boolean;
  fetchTabs?: () => Promise<string[]>;
  fetchList?: (params: { tab: string; page: string }) => Promise<RocheEndpoint<unknown[]>>;
  fetchSearch?: (params: { query: string; page: string; tab?: string }) => Promise<RocheEndpoint<unknown[]>>;
}

export const RouterSearchList = ({
  slot = 'default',
  tabName = 'year',
  pageName = 'page',
  queryName = 'query',
  children = () => <div />,
  searchTabs = false,
  fetchTabs,
  fetchList,
  fetchSearch,
}: SearchListProps): JSX.Element => {
  const { tab = '', query = undefined, page = '1' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const container = useRef(null);
  const scrollHook = useRef(null);
  const scrollHookQuery = useRef(null);
  const resultsLabel = useTranslation('results', { capitalize: true });
  const searchPlaceholder = useTranslation('enter your search term', { capitalize: true });
  const activeTabRef = useRef(tab);
  const activePageRef = useRef(page);
  const activePageResultsRef = useRef(null);
  const debounceTimer = 800;
  const hasTrackedRef = useRef(false);

  const tabsQuery = useQuery({
    queryKey: ['tabs'],
    queryFn: () => fetchTabs(),
  });

  const listQuery = useQuery({
    queryKey: ['list', { page, tab }],
    queryFn: () => fetchList({ page, tab: tab || tabsQuery.data?.[0] }),
    enabled: !!tabsQuery.data && !query,
    placeholderData: keepPreviousData,
  });

  const searchQuery = useQuery({
    queryKey: ['search', { page, query, tab }],
    queryFn: () => fetchSearch({ page, query, tab }),
    enabled: query !== undefined,
    placeholderData: keepPreviousData,
  });

  const prefetch = useCallback(
    ({ page, tab, query }) => {
      if (query) {
        queryClient.prefetchQuery({
          queryKey: ['search', { page, query, tab }],
          queryFn: () => fetchSearch({ page, query, tab }),
          staleTime: 60000,
        });
      } else {
        queryClient.prefetchQuery({
          queryKey: ['list', { page, tab }],
          queryFn: () => fetchList({ page, tab: tab || tabsQuery.data?.[0] }),
          staleTime: 60000,
        });
      }
    },
    [tabsQuery.data],
  );

  const handleTabClick = useCallback(
    (newTab) => {
      let queryParam = '';
      if (searchTabs && query) {
        queryParam = `/${queryName}/${query}`;
      }

      activeTabRef.current = newTab;
      activePageRef.current = '1';

      setTimeout(() => {
        trackEvent();
      }, debounceTimer);

      navigate(`/${tabName}/${newTab}${queryParam}/${pageName}/1`);
    },
    [page, query],
  );

  const handlePagingClick = useCallback(
    (newPage) => {
      if (query !== undefined) {
        let tabParam = '';
        if (searchTabs) {
          tabParam = `/${tabName}/${currentTab}`;
        }
        navigate(`${tabParam}/${queryName}/${query}/${pageName}/${newPage}`);
        scrollHookQuery.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        navigate(`/${tabName}/${tab || tabsQuery.data?.[0]}/${pageName}/${newPage}`);
        scrollHook.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        activePageRef.current = newPage;
        trackEvent();
      }
    },
    [tab, query, tabsQuery.data],
  );

  const handleResetSearchClick = useCallback(() => {
    navigate(`/${tabName}/${tab || tabsQuery.data?.[0]}/${pageName}/1`);
    setSearchInputValue('');
  }, [tab, tabsQuery.data]);

  const getPageCount = useCallback(() => {
    if (query) {
      return searchQuery.isSuccess ? Math.ceil(searchQuery.data?.Total / 10) : 0;
    }
    return listQuery.isSuccess ? Math.ceil(listQuery.data?.Total / 10) : 0;
  }, [query, searchQuery, listQuery]);

  const getCurrentQuery = useCallback(() => {
    if (query) {
      return searchQuery;
    }
    return listQuery;
  }, [query, searchQuery, listQuery]);

  const currentQuery = getCurrentQuery();

  const currentTab = tab || tabsQuery.data?.[0];
  const [searchInputValue, setSearchInputValue] = useState(query || '');

  const onChange = (event) => {
    setSearchInputValue(event.target.value);
  };

  useEffect(() => {
    if (query) {
      setSearchInputValue(query);
    } else {
      setSearchInputValue('');
    }
  }, [query]);

  useDebounce(
    () => {
      if (searchInputValue && searchInputValue !== query && fetchSearch) {
        let tabParam = '';
        if (searchTabs) {
          tabParam = `/${tabName}/${currentTab}`;
        }
        navigate(`${tabParam}/${queryName}/${searchInputValue}/${pageName}/1`);
        trackEvent();
      }
      if (searchInputValue === '' && query) {
        navigate(`/${tabName}/${tab || tabsQuery.data?.[0]}/${pageName}/1`);
      }
    },
    debounceTimer,
    [searchInputValue, currentTab],
  );

  const showTabs = useCallback(() => {
    if (query === undefined) {
      return true;
    }
    if (searchTabs) {
      return true;
    }
    return false;
  }, [query, searchTabs]);

  const trackEvent = () => {
    const adobeTrackingData: OnSiteSearch = {
      term: searchInputValue,
      results: activePageResultsRef.current,
      searchComponent: 'Media Releases Search',
      filter: {
        pagination: parseInt(activePageRef.current, 10),
        filter: !hasTrackedRef.current ? currentTab : activeTabRef.current,
      },
    };

    AdobeTracking(adobeTrackingData, 'onsiteSearch');
  };

  if (currentQuery.data?.Items.length) {
    activePageResultsRef.current = currentQuery.data.Total;
  }

  useEffect(() => {
    if (currentQuery.isSuccess && !hasTrackedRef.current) {
      trackEvent();
      hasTrackedRef.current = true;
    }
  }, [currentQuery.isSuccess, currentTab]);

  return (
    <roche-search-list-container slot={slot} ref={container}>
      <div
        style={{
          marginTop: 'calc((var(--header-height) + 20px) * -1)',
          position: 'absolute',
        }}
        ref={scrollHookQuery}
      ></div>
      {tabsQuery.isLoading && <roche-loading-spinner></roche-loading-spinner>}
      {fetchSearch && (
        <roche-search-input>
          <input
            value={searchInputValue}
            onChange={onChange}
            type='search'
            autoComplete='off'
            placeholder={searchPlaceholder}
          />
          {searchInputValue && (
            // refactor me once roche-search-input accepts children here;
            <roche-icon onClick={() => handleResetSearchClick()} icon='remove' height='1.5rem'></roche-icon>
          )}
        </roche-search-input>
      )}
      <div
        style={{
          marginTop: 'calc((var(--header-height) + 20px) * -1)',
          position: 'absolute',
        }}
        ref={scrollHook}
      ></div>
      {showTabs() && (
        <roche-tab-nav-container>
          {tabsQuery.data?.map((item, index) => (
            <roche-tab-nav-button
              onClick={() => handleTabClick(item)}
              key={`tab-${item}-${index}`}
              onMouseEnter={() => prefetch({ page: 1, tab: item, query })}
              onFocus={() => prefetch({ page: 1, tab: item, query })}
              {...(currentTab === item ? { active: true } : {})}
            >
              {item}
            </roche-tab-nav-button>
          ))}
        </roche-tab-nav-container>
      )}
      {currentQuery.data?.Items.length ? (
        <>
          <p className='results'>
            {currentQuery.data.Total} {resultsLabel}
          </p>
          <div className='results' style={{ opacity: currentQuery.isPlaceholderData ? 0.3 : 1 }}>
            {children({ items: currentQuery.data.Items })}
          </div>
        </>
      ) : (
        !currentQuery.isLoading && <roche-empty-state></roche-empty-state>
      )}
      {currentQuery.isLoading && <roche-loading-spinner></roche-loading-spinner>}

      {getPageCount() > 1 && (
        <Pagination
          pageCount={getPageCount()}
          activePage={parseInt(page, 10)}
          onClick={(newPage) => handlePagingClick(newPage)}
          onMouseEnter={(item) => prefetch({ page: item, tab: currentTab, query })}
          onFocus={(item) => prefetch({ page: item, tab: currentTab, query })}
          resultsPerPageCount={10}
        />
      )}
    </roche-search-list-container>
  );
};
